import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiResponse,
  CreatePatientDto,
  EnablePortalDto,
  EnablePortalResult,
  PatientCsvImportExecuteResponse,
  PatientCsvImportPreviewResponse,
  PatientCsvImportMode,
  PatientProfileResponse,
  PatientsListQuery,
  PatientsListResponse,
  UpdatePatientDto
} from '../models/patient';
import { Observable } from 'rxjs';
import { baseURL } from '../constants/base.url';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  constructor(private http: HttpClient) { }

  list(query: PatientsListQuery): Observable<ApiResponse<PatientsListResponse>> {
    let params = new HttpParams();

    Object.entries(query || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      params = params.set(k, String(v));
    });

    return this.http.get<ApiResponse<PatientsListResponse>>(`${baseURL}patients`, { params });
  }

  getProfile(id: string): Observable<ApiResponse<PatientProfileResponse>> {
    return this.http.get<ApiResponse<PatientProfileResponse>>(`${baseURL}patients/${id}`);
  }

  create(dto: CreatePatientDto): Observable<ApiResponse<{ patient: any }>> {
    return this.http.post<ApiResponse<{ patient: any }>>(`${baseURL}patients`, dto);
  }

  update(id: string, dto: UpdatePatientDto): Observable<ApiResponse<{ patient: any }>> {
    return this.http.patch<ApiResponse<{ patient: any }>>(`${baseURL}patients/${id}`, dto);
  }

  archive(id: string): Observable<ApiResponse<{ patient: any }>> {
    return this.http.post<ApiResponse<{ patient: any }>>(`${baseURL}patients/${id}/archive`, {});
  }

  // Portal actions (Option B)
  enablePortal(id: string, dto: EnablePortalDto): Observable<ApiResponse<EnablePortalResult>> {
    return this.http.post<ApiResponse<EnablePortalResult>>(`${baseURL}patients/${id}/portal/enable`, dto);
  }

  resetPortalPin(id: string, newPin: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${baseURL}patients/${id}/portal/reset-pin`, { newPin });
  }

  disablePortal(id: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${baseURL}patients/${id}/portal/disable`, {});
  }
  
  // CSV
  downloadImportTemplate(): Observable<Blob> {
    return this.http.get(`${baseURL}patients/import/template`, {
      responseType: 'blob'
    });
  }

  previewImport(
    file: File,
    mapping?: Record<string, string>
  ): Observable<ApiResponse<PatientCsvImportPreviewResponse>> {
    const formData = new FormData();
    formData.append('file', file);

    if (mapping && Object.keys(mapping).length) {
      formData.append('mapping', JSON.stringify(mapping));
    }

    return this.http.post<ApiResponse<PatientCsvImportPreviewResponse>>(
      `${baseURL}patients/import/preview`,
      formData
    );
  }

  executeImport(
    file: File,
    mode: PatientCsvImportMode,
    mapping?: Record<string, string>
  ): Observable<ApiResponse<PatientCsvImportExecuteResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);

    if (mapping && Object.keys(mapping).length) {
      formData.append('mapping', JSON.stringify(mapping));
    }

    return this.http.post<ApiResponse<PatientCsvImportExecuteResponse>>(
      `${baseURL}patients/import/execute`,
      formData
    );
  }
}
